import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    TemplateRef,
    computed,
    contentChildren,
    inject,
    input,
    signal,
} from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { QpxCmdkItem } from './item';
import type { CmdkContent } from './models';

@Component({
    selector: 'qpx-cmdk-group',
    imports: [NgTemplateOutlet],
    templateUrl: './group.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'qpx-cmdk-group',
        '[id]': 'groupId',
        '[class.active]': 'active()',
        '[class.hidden]': '!showGroup()',
        '[attr.data-value]': 'dataValue()',
    },
})
export class QpxCmdkGroup {
    //#region Dependency injection
    private readonly cdr = inject(ChangeDetectorRef);
    //#endregion

    //#region Inputs
    public readonly label = input<CmdkContent | undefined>();
    public readonly ariaLabel = input<string | undefined>();
    //#endregion

    //#region Queries
    private readonly items = contentChildren(QpxCmdkItem, {
        descendants: true,
    });
    //#endregion

    //#region State
    public readonly groupId = `cmdk-group-${uuidv4()}`;
    protected readonly active = signal(false);
    protected readonly showGroup = signal(true);
    //#endregion

    //#region Derived state
    public readonly filteredItems = computed(() =>
        this.items().filter((item) => item.filtered())
    );
    protected readonly labelTemplate = computed(() => {
        const label = this.label();
        return label instanceof TemplateRef ? label : undefined;
    });
    protected readonly labelText = computed(() => {
        const label = this.label();
        return typeof label === 'string' ? label : undefined;
    });
    protected readonly dataValue = computed(() =>
        this.label()?.toString().toLowerCase()
    );
    public readonly filtered = computed(() => this.filteredItems().length > 0);
    //#endregion

    //#region Public methods
    public setActive(value: boolean): void {
        this.active.set(value);
        this.cdr.markForCheck();
    }

    public setShowGroup(value: boolean): void {
        this.showGroup.set(value);
        this.cdr.markForCheck();
    }
    //#endregion
}
